import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverHeader, PopoverBody, Spinner } from "reactstrap";
import { queryNoTracking, update } from "../../services/DataService";
import { ParameterValueTree } from "../../dialogs/SetTemplateParameterValuesDlg";
import { toastError, t } from "../../services/HelperService";

const query_ChatAssignees = queryNoTracking("Chat_GetAssignees");

export default function AssigneePickerPopover({
    open,                 // boolean
    anchorId,             // e.g. "assignee-123"
    chatId,               // GUID or number
    onClose,              // () => void
    onCommitted,          // ({ kind: 'Team'|'User', id: guid, name: string }) => void
    onAssignToMe,         // () => Promise<void> | void
}) {
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);
    const [buckets, setBuckets] = useState([]);
    const [selectedId, setSelectedId] = useState(null); // "team:<guid>" | "user:<guid>"
    const [saving, setSaving] = useState(false);

    // Keep mounted long enough to play fade-out
    const [visible, setVisible] = useState(open);
    useEffect(() => {
        if (open) {
            setVisible(true);
            return;
        }
        const t = setTimeout(() => setVisible(false), 200); // ~Bootstrap fade duration
        return () => clearTimeout(t);
    }, [open]);

    // Ref to the actual popover element
    const popoverRef = useRef(null);

    // Close on true outside click (bubble phase) and on Escape
    useEffect(() => {
        if (!open) return;

        const handleDocMouseDown = (e) => {
            const anchorEl = anchorId ? document.getElementById(anchorId) : null;
            const popEl =
                popoverRef.current ||
                // fallback in case innerRef isn't set yet
                document.querySelector(".popover");

            const t = e.target;
            const clickedInsidePopover =
                !!popEl && (popEl === t || popEl.contains(t) || t.closest?.(".popover"));
            const clickedAnchor = !!anchorEl && (anchorEl === t || anchorEl.contains(t));

            if (clickedInsidePopover || clickedAnchor) return; // don't close
            onClose?.();
        };

        const handleKey = (e) => {
            if (e.key === "Escape") onClose?.();
        };

        document.addEventListener("mousedown", handleDocMouseDown); // bubble, not capture
        document.addEventListener("keydown", handleKey);
        return () => {
            document.removeEventListener("mousedown", handleDocMouseDown);
            document.removeEventListener("keydown", handleKey);
        };
    }, [open, anchorId, onClose]);

    useEffect(() => {
        if (!open || !chatId) return;
        setLoading(true); setErr(null); setSelectedId(null);
        (async () => {
            try {
                const res = await query_ChatAssignees.getAll();
                const rows = Array.isArray(res) ? res : (res?.data ?? []);
                setBuckets(rows || []);
            } catch (e) {
                console.error("Chat_GetAssignees failed", e);
                setErr(t("application_strings.views.chat.assigneePicker.loadFailed"));
            } finally {
                setLoading(false);
            }
        })();
    }, [open, chatId]);

    const treeData = useMemo(
        () =>
            (Array.isArray(buckets) ? buckets : []).map((b) => ({
                id: `team:${b.TeamId}`,
                text: b.TeamName || t("application_strings.views.chat.assigneePicker.untitledTeam"),
                items: (b.Representatives || []).map((r) => ({
                    id: `user:${r.RepresentativeId}`,
                    text: r.RepresentativeName,
                })),
            })),
        [buckets]
    );

    const findNode = (nodes, id) => {
        for (const n of nodes || []) {
            if (n.id === id) return n;
            const child = findNode(n.items, id);
            if (child) return child;
        }
        return null;
    };

    const handleSelect = async () => {
        if (!selectedId || !chatId) return;
        const [kind, raw] = String(selectedId).split(":"); // raw is GUID
        const node = findNode(treeData, selectedId);
        try {
            setSaving(true);
            const ok = await update("Chat_SetAssignee", {
                ChatId: chatId,
                TeamId: kind === "team" ? raw : null,
                RepresentativeId: kind === "user" ? raw : null,
            });
            if (!ok) throw new Error("Server returned false");
            onCommitted?.({
                kind: kind === "team" ? "Team" : "User",
                id: raw,
                name: node?.text || "",
            });
            onClose?.();
        } catch (e) {
            console.error("SetAssignee failed", e);
            toastError(t("application_strings.views.chat.assigneePicker.errors.setAssigneeFailed"));
        } finally {
            setSaving(false);
        }
    };

    const handleAssignToMe = async () => {
        if (!onAssignToMe) return;
        try {
            setSaving(true);
            await onAssignToMe();
            onClose?.();
        } catch (e) {
            console.error("AssignToMe failed", e);
            toastError(t("application_strings.views.chat.assigneePicker.errors.assignToMeFailed"));
        } finally {
            setSaving(false);
        }
    };

    if (!visible) return null;

    return (
        <Popover
            isOpen={open}
            target={anchorId}
            placement="bottom"
            toggle={onClose}
            fade={true}
            innerRef={popoverRef}
        >
            <PopoverHeader>{t("application_strings.views.chat.assigneePicker.header")}</PopoverHeader>
            <PopoverBody
                style={{ maxHeight: 280, overflowY: "auto", minWidth: 260 }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                {loading && (
                    <div className="d-flex align-items-center gap-2">
                        <Spinner size="sm" /> {t("application_strings.views.chat.assigneePicker.loading")}
                    </div>
                )}
                {err && <div className="text-danger">{err}</div>}
                {!loading && !err && treeData.length === 0 && (
                    <div className="text-muted">{t("application_strings.views.chat.assigneePicker.empty")}</div>
                )}
                {!loading && !err && treeData.length > 0 && (
                    <>
                        <ParameterValueTree
                            data={treeData}
                            value={null}
                            onChange={(id) => setSelectedId(id)}
                        />
                        <div className="d-flex justify-content-end gap-2 mt-2">
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={onClose}
                            >
                                {t("application_strings.views.chat.assigneePicker.buttons.close")}
                            </button>
                            {!!onAssignToMe && !loading && !err && (
                                <>
                                    <div className="d-flex justify-content-end">
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            disabled={saving}
                                            onClick={handleAssignToMe}
                                        >
                                            {t("application_strings.views.chat.assigneePicker.buttons.assignToMe")}
                                        </button>
                                    </div>
                                </>
                            )}
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                disabled={!selectedId || saving}
                                onClick={handleSelect}
                            >
                                {saving ? t("application_strings.views.chat.assigneePicker.buttons.saving") : t("application_strings.views.chat.assigneePicker.buttons.select")}
                            </button>
                        </div>
                    </>
                )}
            </PopoverBody>
        </Popover>
    );
}
